/**
 * IntelliHealthRightPanel
 *
 * Workstream C — Docked IntelliHealth Agent panel for the Three-Panel Console.
 *
 * Replaces the floating ChatFAB + drawer pattern with a persistent, collapsible
 * right panel that is context-aware of the current patient. Inspired by Medplum
 * Spaces + Canvas AI Agents.
 *
 * PRD Reference: Section 4C → Right Panel — IntelliHealth Agent
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Sparkles,
  PanelRightClose,
  PanelRightOpen,
  MessageSquarePlus,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { useIntelliHealthChat } from '@/contexts/IntelliHealthChatContext';
import MessageList, { type DisplayMessage } from '@/components/intellihealth/chat/MessageList';
import ChatInput from '@/components/intellihealth/chat/ChatInput';
import ConnectionStatusBanner from '@/components/intellihealth/chat/ConnectionStatus';
import ConversationSidebar from '@/components/intellihealth/chat/ConversationSidebar';
import CDSTriagePanel from '@/components/intellihealth/CDSTriagePanel';
import { cn } from '@/lib/utils';
import { aiRecommendationService } from '@/services/aiRecommendationService';
import { toast } from '@/hooks/use-toast';

// ============================================================================
// Types
// ============================================================================

interface IntelliHealthRightPanelProps {
  patientId: string | null;
  patientName: string | null;
  collapsed: boolean;
  onToggleCollapse: () => void;
  /** Surfaced for CDS Triage actions — open existing modals from the chart. */
  medicationAllergies?: string[] | null;
  onPrescribe?: () => void;
  onOrderLab?: () => void;
  onSchedule?: () => void;
}

// ============================================================================
// Prompt chips (context-aware)
// ============================================================================

const PATIENT_PROMPT_CHIPS = [
  'Analyze labs',
  'Suggest next steps',
  'Check interactions',
  'Draft SOAP note',
];

// ============================================================================
// Component
// ============================================================================

const IntelliHealthRightPanel: React.FC<IntelliHealthRightPanelProps> = ({
  patientId,
  patientName,
  collapsed,
  onToggleCollapse,
  medicationAllergies,
  onPrescribe,
  onOrderLab,
  onSchedule,
}) => {
  const {
    messages,
    streamingMessage,
    isStreaming,
    isWaitingForResponse,
    connectionStatus,
    prompts,
    rateLimited,
    sendMessage,
    retry,
    connect,
    isDrawerOpen,
    setDrawerOpen,
  } = useIntelliHealthChat();

  const [systemMessages, setSystemMessages] = useState<DisplayMessage[]>([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const lastPatientIdRef = useRef<string | null>(null);

  // Auto-connect when panel mounts or becomes visible
  useEffect(() => {
    if (!collapsed && connectionStatus === 'disconnected') {
      connect();
    }
  }, [collapsed, connectionStatus, connect]);

  // Track patient context changes
  useEffect(() => {
    if (lastPatientIdRef.current === null) {
      lastPatientIdRef.current = patientId;
      return;
    }

    if (patientId !== lastPatientIdRef.current) {
      const text = patientName
        ? `Now viewing: ${patientName}`
        : patientId
          ? `Now viewing patient: ${patientId}`
          : 'No patient selected';

      setSystemMessages((prev) => [
        ...prev,
        {
          id: `sys-${Date.now()}`,
          type: 'system',
          content: text,
        },
      ]);
      lastPatientIdRef.current = patientId;
    }
  }, [patientId, patientName]);

  const handleSend = useCallback(
    (text: string) => {
      sendMessage(text, patientId ?? undefined, undefined);
    },
    [sendMessage, patientId],
  );

  const handlePromptChip = useCallback(
    (text: string) => {
      handleSend(text);
    },
    [handleSend],
  );

  // Header sync button state (JJ ask: proactive sync trigger from side panel,
  // separate from the inline-in-chat "Force Sync" button which only renders
  // when the AI specifically prompts for it).
  const [headerSyncing, setHeaderSyncing] = useState(false);
  const handleHeaderSync = useCallback(async () => {
    if (!patientId || headerSyncing) return;
    setHeaderSyncing(true);
    try {
      await aiRecommendationService.syncPatientToIntellihealth(patientId);
      toast({
        title: 'Patient data synced',
        description: 'IntelliHealth has fresh data for this patient.',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      toast({
        title: 'Sync Failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setHeaderSyncing(false);
    }
  }, [patientId, headerSyncing]);

  // Inline sync handler for "Force Sync" prompts in chat messages (JJ pick 1A)
  const handleSyncPatient = useCallback(async () => {
    if (!patientId) return;
    try {
      await aiRecommendationService.syncPatientToIntellihealth(patientId);
      toast({
        title: 'Patient data synced',
        description: 'You can now ask your question again.',
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      toast({
        title: 'Sync Failed',
        description: message,
        variant: 'destructive',
      });
      throw err; // Re-throw so the button shows error state
    }
  }, [patientId]);

  const inputDisabled =
    isStreaming || isWaitingForResponse || connectionStatus !== 'connected' || rateLimited;
  const showWelcome = messages.length === 0 && !isStreaming;

  // Collapsed strip — just an icon column
  if (collapsed) {
    return (
      <div className="flex flex-col items-center w-10 border-l border-slate-200 bg-slate-50 py-4">
        <button
          onClick={onToggleCollapse}
          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
          title="Open IntelliHealth®"
        >
          <PanelRightOpen className="h-4 w-4" />
        </button>
        <div className="mt-3">
          <Sparkles className="h-4 w-4 text-brand-teal" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-[360px] border-l border-slate-200 bg-white flex-shrink-0">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-brand-teal flex-shrink-0" />
          <h3 className="text-sm font-semibold text-slate-900 truncate">
            IntelliHealth®
          </h3>
          {patientName && (
            <span className="text-xs text-slate-400 truncate">· {patientName}</span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          {patientId && (
            <button
              onClick={handleHeaderSync}
              disabled={headerSyncing}
              className={cn(
                'rounded-md p-1.5 transition-colors',
                headerSyncing
                  ? 'text-brand-teal cursor-wait'
                  : 'text-slate-400 hover:text-brand-teal hover:bg-slate-100',
              )}
              title={headerSyncing ? 'Syncing patient data…' : 'Force sync — pull latest patient data into IntelliHealth'}
              aria-label="Force sync patient data"
            >
              {headerSyncing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </button>
          )}
          <button
            onClick={() => setShowSidebar((v) => !v)}
            className="rounded-md p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            title="Conversations"
          >
            <MessageSquarePlus className="h-4 w-4" />
          </button>
          <button
            onClick={onToggleCollapse}
            className="rounded-md p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            title="Collapse panel"
          >
            <PanelRightClose className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Connection status */}
      <ConnectionStatusBanner status={connectionStatus} onRetry={retry} />

      {/* Rate limit */}
      {rateLimited && (
        <div className="px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border-b border-amber-200">
          Rate limit reached. Please wait.
        </div>
      )}

      {/* Scrollable area: proactive CDS triage on top, chat below */}
      <div className="relative flex-1 flex flex-col overflow-y-auto">
        <ConversationSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />

        {/*
          Right rail CDS is the *background queue* — INFO-only passive
          context. Active reasoning (ACTION/WATCH/BLOCKED) lives in the
          center column where the prescribe action terminates. Pattern
          matches Epic BPA / Cerner mPages / athenahealth conventions.
        */}
        <CDSTriagePanel
          patientId={patientId}
          patientName={patientName}
          medicationAllergies={medicationAllergies}
          severityFilter={['info']}
          mode="rail"
          onPrescribe={onPrescribe}
          onOrderLab={onOrderLab}
          onSchedule={onSchedule}
        />

        {showWelcome ? (
          <div className="flex-1 flex flex-col items-center justify-center px-4 text-center">
            <Sparkles className="w-8 h-8 text-brand-teal mb-3" />
            <p className="text-sm text-slate-500 mb-4">
              {patientName
                ? `Ask about ${patientName}`
                : 'Ask IntelliHealth® a question'}
            </p>
            {/* Prompt chips */}
            <div className="flex flex-wrap justify-center gap-1.5">
              {(prompts.length > 0
                ? prompts.map((p) => p.text)
                : PATIENT_PROMPT_CHIPS
              ).map((chipText, i) => (
                <button
                  key={i}
                  onClick={() => handlePromptChip(chipText)}
                  disabled={inputDisabled}
                  className={cn(
                    'text-xs px-3 py-1.5 rounded-lg border bg-white transition-all',
                    inputDisabled
                      ? 'border-slate-100 text-slate-300 cursor-not-allowed'
                      : 'border-slate-200 text-slate-600 hover:border-brand-teal hover:text-brand-teal hover:shadow-sm'
                  )}
                >
                  {chipText}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <MessageList
            messages={messages}
            streamingMessage={streamingMessage}
            isStreaming={isStreaming}
            isWaitingForResponse={isWaitingForResponse}
            systemMessages={systemMessages}
            onSyncPatient={handleSyncPatient}
          />
        )}
      </div>

      {/* Input */}
      <div className="border-t border-slate-200 p-2.5">
        <ChatInput
          onSend={handleSend}
          disabled={inputDisabled}
          placeholder={
            rateLimited
              ? 'Rate limit reached...'
              : connectionStatus !== 'connected'
                ? 'Connecting...'
                : patientName
                  ? `Ask about ${patientName}...`
                  : 'Ask IntelliHealth®...'
          }
        />
      </div>
    </div>
  );
};

export default IntelliHealthRightPanel;
